// signals-web.ts — the WEB runtime's ledger signals.
//
// 🔴 WHY THIS EXISTS: `compute_signals` was a zero stub in web-commands.ts. On web the whole
// "Year in Live Music" recap — the app's one billed feature — was handed a ledger of zeros: no
// shows, no artists, no spend. It would have narrated a year of gigs it could not see. Native is
// unaffected (Rust computes it).
//
// Every rule below mirrors `src-tauri/src/signals.rs`. Where the two could drift, the Rust wins —
// it is the shipping surface. If you change a threshold here, change it there.
//
// `lead_insights` is ported in full and deliberately: the recap OPENS with lead_insights[0], and
// those are the only findings that need two columns correlated (rating × cost, venue × rating,
// city × time). Without them the recap can only restate the bars already on screen — which is
// exactly the difference between a paid draw and a re-run of the free view.

import type { Show, LedgerSignals } from "./store";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MAX_NAMES = 8;

const round1 = (x: number) => Math.round(x * 10) / 10;
const round2 = (x: number) => Math.round(x * 100) / 100;

function parse(s: string | null | undefined): Date | null {
  if (!s) return null;
  const head = s.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return null;
  const t = Date.parse(`${head}T00:00:00Z`);
  return Number.isNaN(t) ? null : new Date(t);
}
const ymd = (d: Date) => d.toISOString().slice(0, 10);
const days = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / 86400000);

/** count desc, then name asc — the Rust's stable tie-break, mirrored everywhere it counts. */
function topNames(m: Map<string, number>, cap = MAX_NAMES) {
  return [...m.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, cap);
}

interface Dated { d: Date; s: Show }

/** Mirror of signals.rs::year_stat. */
function yearStat(year: number, shows: Show[]) {
  const artists = new Map<string, number>();
  const venues = new Set<string>();
  const monthAcc = new Array(12).fill(0) as number[];
  let festivalCount = 0, spend = 0, hasCost = false;

  for (const s of shows) {
    if (s.is_festival) festivalCount += 1;
    if (typeof s.cost === "number") { spend += s.cost; hasCost = true; }
    for (const a of s.artists ?? []) artists.set(a, (artists.get(a) ?? 0) + 1);
    if ((s.venue ?? "").trim()) venues.add(s.venue);
    const d = parse(s.seen_on);
    if (d) monthAcc[d.getUTCMonth()] += 1;
  }

  const top = topNames(artists, 1)[0];
  let bestI = 0, bestC = -1;
  monthAcc.forEach((c, i) => { if (c > bestC) { bestC = c; bestI = i; } });

  return {
    year,
    shows: shows.length,
    distinct_artists: artists.size,
    distinct_venues: venues.size,
    festival_count: festivalCount,
    spend_cents: hasCost ? spend : null,
    busiest_month: bestC > 0 ? MONTHS[bestI] : null,
    top_artist: top ? top.name : null,
  };
}

/** Mirror of signals.rs::compute. */
export function computeSignalsWeb(all: Show[], todayStr: string): LedgerSignals {
  const dated: Dated[] = all
    .map((s) => ({ d: parse(s.seen_on), s }))
    .filter((x): x is Dated => x.d !== null);

  // Dominant currency (mode; default USD) — spend phrasing follows the ledger, not a guess.
  const curAcc = new Map<string, number>();
  for (const x of dated) curAcc.set(x.s.currency, (curAcc.get(x.s.currency) ?? 0) + 1);
  const currency = [...curAcc.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? "USD";

  const times = dated.map((x) => x.d.getTime());
  const first = times.length ? new Date(Math.min(...times)) : null;
  const last = times.length ? new Date(Math.max(...times)) : null;

  const artistCounts = new Map<string, number>();
  const venueCounts = new Map<string, number>();
  const cityCounts = new Map<string, number>();
  const companionCounts = new Map<string, number>();
  const years = new Set<number>();
  let festivalCount = 0, totalSpend = 0, showsWithCost = 0;

  for (const { d, s } of dated) {
    years.add(d.getUTCFullYear());
    if (s.is_festival) festivalCount += 1;
    if (typeof s.cost === "number") { totalSpend += s.cost; showsWithCost += 1; }
    for (const a of s.artists ?? []) artistCounts.set(a, (artistCounts.get(a) ?? 0) + 1);
    if ((s.venue ?? "").trim()) venueCounts.set(s.venue, (venueCounts.get(s.venue) ?? 0) + 1);
    if ((s.city ?? "").trim()) cityCounts.set(s.city, (cityCounts.get(s.city) ?? 0) + 1);
    for (const p of s.companions ?? []) companionCounts.set(p, (companionCounts.get(p) ?? 0) + 1);
  }

  const overall = {
    total_shows: dated.length,
    distinct_artists: artistCounts.size,
    distinct_venues: venueCounts.size,
    distinct_cities: cityCounts.size,
    festival_count: festivalCount,
    first_show: first ? ymd(first) : null,
    last_show: last ? ymd(last) : null,
    years_active: years.size,
    total_spend_cents: showsWithCost > 0 ? totalSpend : null,
    shows_with_cost: showsWithCost,
    avg_cost_cents: showsWithCost > 0 ? Math.round(totalSpend / showsWithCost) : null,
    note: "Every figure describes shows the user ALREADY ATTENDED and spend they logged. Names are artists/venues/cities they entered. Nothing here is a recommendation, a resale value, or a ticket to buy.",
  };

  // ── Per-year, newest first ─────────────────────────────────────────────────
  const yearGroups = new Map<number, Show[]>();
  for (const { d, s } of dated) {
    const y = d.getUTCFullYear();
    yearGroups.set(y, [...(yearGroups.get(y) ?? []), s]);
  }
  const by_year = [...yearGroups.entries()]
    .map(([year, shows]) => yearStat(year, shows))
    .sort((a, b) => b.year - a.year);

  // ── Year-over-year shift, the two most recent years with data ─────────────
  let year_shift = null as null | {
    recent_year: number; prior_year: number; shows_delta: number;
    spend_delta_cents: number | null; direction: string;
  };
  if (by_year.length >= 2) {
    const r = by_year[0], p = by_year[1];
    const showsDelta = r.shows - p.shows;
    year_shift = {
      recent_year: r.year, prior_year: p.year, shows_delta: showsDelta,
      spend_delta_cents: r.spend_cents !== null && p.spend_cents !== null ? r.spend_cents - p.spend_cents : null,
      direction: showsDelta > 0 ? "more" : showsDelta < 0 ? "fewer" : "same",
    };
  }

  // ── Spend split: festivals vs regular ─────────────────────────────────────
  let festivalSpend = 0, regularSpend = 0, festivalCostShows = 0, regularCostShows = 0;
  for (const { s } of dated) {
    if (typeof s.cost !== "number") continue;
    if (s.is_festival) { festivalSpend += s.cost; festivalCostShows += 1; }
    else { regularSpend += s.cost; regularCostShows += 1; }
  }
  const spend_breakdown = {
    festival_spend_cents: festivalSpend,
    regular_spend_cents: regularSpend,
    festival_shows_with_cost: festivalCostShows,
    regular_shows_with_cost: regularCostShows,
    bigger_bucket: festivalCostShows === 0 && regularCostShows === 0 ? null
      : festivalSpend > regularSpend ? "festivals"
      : regularSpend > festivalSpend ? "regular shows" : "even",
  };

  // ── Month histogram ───────────────────────────────────────────────────────
  const monthAcc = new Array(12).fill(0) as number[];
  for (const { d } of dated) monthAcc[d.getUTCMonth()] += 1;
  const months = MONTHS.map((m, i) => ({ month: m, shows: monthAcc[i] }));

  // ── Longest gap between consecutive attended DATES (deduped) ──────────────
  const uniqueDates = [...new Set(dated.map((x) => ymd(x.d)))].sort().map((s) => new Date(`${s}T00:00:00Z`));
  let longest_gap = null as null | { from_date: string; to_date: string; days: number };
  for (let i = 1; i < uniqueDates.length; i++) {
    const n = days(uniqueDates[i - 1], uniqueDates[i]);
    if (longest_gap === null || n > longest_gap.days) {
      longest_gap = { from_date: ymd(uniqueDates[i - 1]), to_date: ymd(uniqueDates[i]), days: n };
    }
  }

  // ── Ratings ───────────────────────────────────────────────────────────────
  const dist = [0, 0, 0, 0, 0];
  let ratedSum = 0, ratedN = 0;
  let best: Show | null = null;
  for (const { s } of dated) {
    if (s.rating >= 1 && s.rating <= 5) {
      dist[s.rating - 1] += 1;
      ratedSum += s.rating; ratedN += 1;
      if (best === null || s.rating > best.rating) best = s;
    }
  }
  const ratings = {
    rated_shows: ratedN,
    avg_rating: ratedN > 0 ? round2(ratedSum / ratedN) : null,
    distribution: dist,
    best_rated: best ? {
      artists: best.artists?.[0] ?? "",
      date: (best.seen_on ?? "").slice(0, 10),
      venue: best.venue ?? "",
      rating: best.rating,
    } : null,
  };

  return {
    schema:
      "oriora.encore.v1-signals: derived aggregates over shows the user ALREADY ATTENDED. Counts, per-year breakdown, spend they logged, ratings they gave, and cross-signals that need two columns correlated. lead_insights = the non-obvious findings; LEAD the recap with lead_insights[0] rather than restating the visible totals. OBSERVATIONAL only — describe what they attended, rated and spent; NEVER recommend a show, value a ticket, or predict.",
    generated_for: todayStr,
    currency,
    overall,
    by_year,
    year_shift,
    top_artists: topNames(artistCounts),
    top_venues: topNames(venueCounts),
    top_cities: topNames(cityCounts),
    top_companions: topNames(companionCounts),
    spend_breakdown,
    months,
    longest_gap,
    ratings,
    lead_insights: buildCrossSignals(dated),
  } as unknown as LedgerSignals;
}

/**
 * Mirror of signals.rs::build_cross_signals — findings the on-screen bars cannot show, because
 * each needs two columns correlated. Every threshold here is the Rust's; they exist to stop the
 * app claiming a pattern from three data points.
 */
function buildCrossSignals(dated: Dated[]) {
  const out: { kind: string; headline: string }[] = [];

  // rated shows that ALSO have a cost — both signals needed to say anything about the pair.
  const ratedCost = dated
    .filter(({ s }) => s.rating >= 1 && s.rating <= 5 && typeof s.cost === "number")
    .map(({ s }) => ({ r: s.rating, c: s.cost as number, fest: !!s.is_festival }));
  const mean = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / xs.length;

  // ── 1) rating × festival, with the spend twist ────────────────────────────
  const fest = ratedCost.filter((x) => x.fest).map((x) => x.r);
  const reg = ratedCost.filter((x) => !x.fest).map((x) => x.r);
  if (fest.length >= 3 && reg.length >= 3) {
    const fa = mean(fest), ra = mean(reg);
    if (Math.abs(fa - ra) >= 0.4) {
      const festSpend = ratedCost.filter((x) => x.fest).reduce((a, b) => a + b.c, 0);
      const regSpend = ratedCost.filter((x) => !x.fest).reduce((a, b) => a + b.c, 0);
      const [higher, hiAvg, lowerWord, loAvg] = ra > fa
        ? ["regular shows", ra, "festivals", fa] as const
        : ["festivals", fa, "regular shows", ra] as const;
      const spentMoreOn = festSpend > regSpend ? "festivals" : "regular shows";
      out.push({
        kind: "rating_vs_festival",
        headline: spentMoreOn !== higher
          // The non-obvious twist: rated one bucket higher while spending more on the other.
          ? `Your ${higher} averaged ${round1(hiAvg)} stars — higher than your ${lowerWord} at ${round1(loAvg)} — even though you spent more in total on ${spentMoreOn}.`
          : `Your ${higher} averaged ${round1(hiAvg)} stars, ahead of your ${lowerWord} at ${round1(loAvg)}.`,
      });
    }
  }

  // ── 2) rating × spend, split at the median ticket ─────────────────────────
  if (ratedCost.length >= 6) {
    const costs = ratedCost.map((x) => x.c).sort((a, b) => a - b);
    const median = costs[Math.floor(costs.length / 2)];
    const cheaper = ratedCost.filter((x) => x.c < median).map((x) => x.r);
    const pricier = ratedCost.filter((x) => x.c >= median).map((x) => x.r);
    if (cheaper.length >= 3 && pricier.length >= 3) {
      const ca = mean(cheaper), pa = mean(pricier);
      if (Math.abs(ca - pa) >= 0.4) {
        out.push({
          kind: "rating_vs_spend",
          headline: ca > pa
            ? `Your lower-cost nights rated higher on average (${round1(ca)} stars) than your pricier ones (${round1(pa)} stars).`
            : `Your pricier nights rated higher on average (${round1(pa)} stars) than your lower-cost ones (${round1(ca)} stars).`,
        });
      }
    }
  }

  // ── 3) venue loyalty — the place you return to isn't where the best nights were ──
  {
    const counts = new Map<string, number>();
    const ratingSum = new Map<string, { sum: number; n: number }>();
    for (const { s } of dated) {
      const v = (s.venue ?? "").trim();
      if (!v) continue;
      counts.set(v, (counts.get(v) ?? 0) + 1);
      if (s.rating >= 1 && s.rating <= 5) {
        const e = ratingSum.get(v) ?? { sum: 0, n: 0 };
        e.sum += s.rating; e.n += 1;
        ratingSum.set(v, e);
      }
    }
    // Rust breaks ties on the HIGHER name descending, i.e. the lexically smaller name wins.
    const mostReturned = [...counts.entries()].filter(([, c]) => c >= 3)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    const bestRated = [...ratingSum.entries()].filter(([, e]) => e.n >= 2)
      .map(([v, e]) => [v, e.sum / e.n] as const)
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
    if (mostReturned && bestRated && mostReturned[0] !== bestRated[0]) {
      const mrE = ratingSum.get(mostReturned[0]);
      const mrAvg = mrE ? mrE.sum / mrE.n : null;
      // Only worth saying when the best-rated place genuinely beats the regular haunt.
      if (mrAvg === null || bestRated[1] - mrAvg >= 0.5) {
        out.push({
          kind: "venue_loyalty",
          headline: `You've been back to ${mostReturned[0]} most (${mostReturned[1]} times), but your highest-rated nights were at ${bestRated[0]} (${round1(bestRated[1])} stars on average there).`,
        });
      }
    }
  }

  // ── 4) city shift — recent half vs earlier half ───────────────────────────
  if (dated.length >= 8) {
    const sorted = [...dated].sort((a, b) => a.d.getTime() - b.d.getTime());
    const mid = Math.floor(sorted.length / 2);
    const topCity = (slice: Dated[]) => {
      const m = new Map<string, number>();
      let total = 0;
      for (const { s } of slice) {
        const c = (s.city ?? "").trim();
        if (!c) continue;
        m.set(c, (m.get(c) ?? 0) + 1);
        total += 1;
      }
      const top = [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0];
      return top ? { city: top[0], n: top[1], total } : null;
    };
    const earlier = topCity(sorted.slice(0, mid));
    const recent = topCity(sorted.slice(mid));
    if (earlier && recent && earlier.city !== recent.city && earlier.total >= 3 && recent.total >= 3) {
      const share = recent.total > 0 ? Math.round((recent.n / recent.total) * 100) : 0;
      out.push({
        kind: "city_shift",
        headline: `Lately most of your shows have been in ${recent.city} (${share}% of your recent nights), whereas your earlier shows centred on ${earlier.city}.`,
      });
    }
  }

  return out;
}
